import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import api from "../api/axiosConfig";
import { useAuth } from "./AuthContext";

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();
  const isFetchingRef = useRef(false);
  const lastFetchedRef = useRef(0);

  // 1. Fetch Notifications with throttle & tab visibility check
  const fetchNotifications = useCallback(async (force = false) => {
    if (!user) return; // Don't fetch if not logged in
    
    // Don't poll if document/tab is currently hidden in background
    if (typeof document !== 'undefined' && document.hidden) return;

    // Prevent overlapping requests
    if (isFetchingRef.current) return;

    // Throttle to at most once every 30 seconds unless forced
    const now = Date.now();
    if (!force && now - lastFetchedRef.current < 30000) return;

    try {
      isFetchingRef.current = true;
      const res = await api.get("/notifications");
      lastFetchedRef.current = Date.now();
      if (Array.isArray(res.data)) {
        setNotifications(res.data);
        setUnreadCount(res.data.filter(n => !n.is_read).length);
      }
    } catch (err) {
      // Silently catch to avoid console spam
    } finally {
      isFetchingRef.current = false;
    }
  }, [user]);

  // 2. Purely on-demand notifications - no eager background polling loops
  // Real-time events arrive via Socket.IO; user fetches on bell click or /notifications visit
  useEffect(() => {
    // Only clear notifications on logout
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user]);

  // 3. Preferences fetching on user login
  const [preferences, setPreferences] = useState(null);

  const fetchPreferences = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get("/notifications/preferences");
      if (res.data) {
        setPreferences(res.data);
      }
    } catch (err) {
      // Silently catch to avoid console spam
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      fetchPreferences();
    } else {
      setPreferences(null);
    }
  }, [user, fetchPreferences]);

  // 4. Mark Single as Read
  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      // Optimistic Update
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error(e);
    }
  };

  // 5. Mark ALL as Read
  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all").catch(() => {});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markRead, markAllRead, fetchNotifications, preferences, fetchPreferences }}>
      {children}
    </NotificationContext.Provider>
  );
};